import apiClient from './apiClient';
import type { ArtifactSession } from './artifactsService';
import type {
  DecisionStatus,
  DepositMode,
  DepositResult,
  MyReviewRequest,
  PersonRef,
  Project,
  ProjectCreatePayload,
  ProjectFile,
  ProjectListParams,
  ProjectReview,
  ProjectUpdatePayload,
  ProjectVersion,
  ReviewRequest,
  ReviewRequestCreatePayload,
  StatusChange,
} from '../types/projects';

const PROJECTS_URL = '/projects';

/** File name from a Content-Disposition header, when the browser exposes it. */
function filenameFrom(disposition: unknown, fallback: string): string {
  if (typeof disposition !== 'string') return fallback;
  const match = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(disposition);
  return match?.[1] ? decodeURIComponent(match[1]) : fallback;
}

/** Hands a blob to the browser as a download (the API needs a bearer token: no plain links). */
function saveBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  // Let the click start the download before releasing the object URL.
  window.setTimeout(() => URL.revokeObjectURL(url), 1000);
}

const baseName = (path: string): string => path.slice(path.lastIndexOf('/') + 1) || path;

export const projectService = {
  list: async (params: ProjectListParams = {}): Promise<Project[]> => {
    const { data } = await apiClient.get<Project[]>(`${PROJECTS_URL}/`, { params });
    return data;
  },

  /** Names of ids cited by analyses (`ent_…`, uuids, `owl:…`), limited to what the caller may read. */
  ontologyLookup: async (ids: string[]): Promise<Record<string, string>> => {
    const { data } = await apiClient.get<{ labels: Record<string, string> }>(`${PROJECTS_URL}/ontology-lookup`, {
      params: { ids: ids.join(',') },
    });
    return data.labels;
  },

  get: async (id: string): Promise<Project> => {
    const { data } = await apiClient.get<Project>(`${PROJECTS_URL}/${id}`);
    return data;
  },

  create: async (payload: ProjectCreatePayload): Promise<Project> => {
    const { data } = await apiClient.post<Project>(`${PROJECTS_URL}/`, payload);
    return data;
  },

  update: async (id: string, payload: ProjectUpdatePayload): Promise<Project> => {
    const { data } = await apiClient.patch<Project>(`${PROJECTS_URL}/${id}`, payload);
    return data;
  },

  /** Moves the project to the trash (restorable). */
  remove: async (id: string): Promise<void> => {
    await apiClient.delete(`${PROJECTS_URL}/${id}`);
  },

  trash: async (): Promise<Project[]> => {
    const { data } = await apiClient.get<Project[]>(`${PROJECTS_URL}/trash`);
    return data;
  },

  restore: async (id: string): Promise<Project> => {
    const { data } = await apiClient.post<Project>(`${PROJECTS_URL}/${id}/restore`);
    return data;
  },

  publish: async (id: string): Promise<Project> => {
    const { data } = await apiClient.post<Project>(`${PROJECTS_URL}/${id}/publish`);
    return data;
  },

  unpublish: async (id: string): Promise<Project> => {
    const { data } = await apiClient.delete<Project>(`${PROJECTS_URL}/${id}/publish`);
    return data;
  },

  /**
   * Uploads files (zips are kept and unpacked server-side). `main` is a deposited path;
   * `folder` puts everything under a sub-folder of the project (created on the fly).
   */
  deposit: async (
    id: string,
    files: File[],
    mode: DepositMode,
    main?: string | null,
    folder?: string | null
  ): Promise<DepositResult> => {
    const form = new FormData();
    files.forEach((file) => form.append('files', file, file.name));
    if (main) form.append('main', main);
    const { data } = await apiClient.post<DepositResult>(`${PROJECTS_URL}/${id}/versions`, form, {
      params: folder ? { mode, folder } : { mode },
    });
    return data;
  },

  /** Newest first. */
  versions: async (id: string): Promise<ProjectVersion[]> => {
    const { data } = await apiClient.get<ProjectVersion[]>(`${PROJECTS_URL}/${id}/versions`);
    return data;
  },

  versionFiles: async (id: string, version: number): Promise<ProjectFile[]> => {
    const { data } = await apiClient.get<ProjectFile[]>(
      `${PROJECTS_URL}/${id}/versions/${version}/files`
    );
    return data;
  },

  currentFiles: async (id: string): Promise<ProjectFile[]> => {
    const { data } = await apiClient.get<ProjectFile[]>(`${PROJECTS_URL}/${id}/files`);
    return data;
  },

  /** Sets (or clears with null) the main file of the current version. */
  /** Removes a file from the current version (older versions keep it); returns the current files. */
  removeFile: async (id: string, documentId: number): Promise<ProjectFile[]> => {
    const { data } = await apiClient.delete<ProjectFile[]>(`${PROJECTS_URL}/${id}/files/${documentId}`);
    return data;
  },

  /** Removes a folder and everything under it from the current version; returns the current files. */
  removeFolder: async (id: string, path: string): Promise<ProjectFile[]> => {
    const { data } = await apiClient.delete<ProjectFile[]>(`${PROJECTS_URL}/${id}/folders`, { params: { path } });
    return data;
  },

  setMain: async (id: string, documentId: number | null): Promise<ProjectFile[]> => {
    const { data } = await apiClient.put<ProjectFile[]>(`${PROJECTS_URL}/${id}/main`, {
      document_id: documentId,
    });
    return data;
  },

  fileText: async (id: string, documentId: number): Promise<string> => {
    const { data } = await apiClient.get<{ text: string }>(
      `${PROJECTS_URL}/${id}/files/${documentId}/text`
    );
    return data.text;
  },

  /** Raw file content (Player, downloads). */
  fileContent: async (id: string, documentId: number): Promise<Blob> => {
    const { data } = await apiClient.get<Blob>(`${PROJECTS_URL}/${id}/files/${documentId}/content`, {
      responseType: 'blob',
    });
    return data;
  },

  downloadFile: async (id: string, file: ProjectFile): Promise<void> => {
    const blob = await projectService.fileContent(id, file.document_id);
    saveBlob(blob, baseName(file.path));
  },

  downloadArchive: async (id: string, version: number, fallbackName: string): Promise<void> => {
    const response = await apiClient.get<Blob>(
      `${PROJECTS_URL}/${id}/versions/${version}/archive`,
      { responseType: 'blob' }
    );
    saveBlob(response.data, filenameFrom(response.headers['content-disposition'], fallbackName));
  },

  // -- comments ---------------------------------------------------------------

  reviews: async (id: string): Promise<ProjectReview[]> => {
    const { data } = await apiClient.get<ProjectReview[]>(`${PROJECTS_URL}/${id}/reviews`);
    return data;
  },

  addReview: async (id: string, text: string): Promise<ProjectReview> => {
    const { data } = await apiClient.post<ProjectReview>(`${PROJECTS_URL}/${id}/reviews`, { text });
    return data;
  },

  updateReview: async (reviewId: string, text: string): Promise<ProjectReview> => {
    const { data } = await apiClient.patch<ProjectReview>(`${PROJECTS_URL}/reviews/${reviewId}`, {
      text,
    });
    return data;
  },

  deleteReview: async (reviewId: string): Promise<void> => {
    await apiClient.delete(`${PROJECTS_URL}/reviews/${reviewId}`);
  },

  // -- workflow (lot L3) -----------------------------------------------------

  /** Lifecycle history, oldest first. */
  history: async (id: string): Promise<StatusChange[]> => {
    const { data } = await apiClient.get<StatusChange[]>(`${PROJECTS_URL}/${id}/history`);
    return data;
  },

  /** Owner only, from « Analysé »: the project goes to the portfolio managers. */
  propose: async (id: string, answers: Record<string, string> = {}): Promise<Project> => {
    const { data } = await apiClient.post<Project>(`${PROJECTS_URL}/${id}/proposal`, { answers });
    return data;
  },

  /** Qualifiers only: proposed → reviewing → qualified (and back to reviewing). */
  setStatus: async (id: string, status: DecisionStatus): Promise<Project> => {
    const { data } = await apiClient.post<Project>(`${PROJECTS_URL}/${id}/status`, { status });
    return data;
  },

  reviewRequests: async (id: string): Promise<ReviewRequest[]> => {
    const { data } = await apiClient.get<ReviewRequest[]>(`${PROJECTS_URL}/${id}/review-requests`);
    return data;
  },

  /** Returns the project's full request list afterwards. */
  requestReviews: async (id: string, payload: ReviewRequestCreatePayload): Promise<ReviewRequest[]> => {
    const { data } = await apiClient.post<ReviewRequest[]>(`${PROJECTS_URL}/${id}/review-requests`, payload);
    return data;
  },

  cancelReviewRequest: async (requestId: string): Promise<void> => {
    await apiClient.delete(`${PROJECTS_URL}/review-requests/${requestId}`);
  },

  /** Requests addressed to the caller, newest first. */
  myReviewRequests: async (): Promise<MyReviewRequest[]> => {
    const { data } = await apiClient.get<MyReviewRequest[]>(`${PROJECTS_URL}/review-requests`);
    return data;
  },

  /** Up to 20 active org members who could be asked (owner or qualifier only). */
  reviewerCandidates: async (id: string, q: string): Promise<PersonRef[]> => {
    const { data } = await apiClient.get<PersonRef[]>(`${PROJECTS_URL}/${id}/reviewer-candidates`, {
      params: q.trim() ? { q: q.trim() } : {},
    });
    return data;
  },

  // -- GenieArtifact ----------------------------------------------------------

  /** The current user's GenieArtifact sessions started from this project, most recent first. */
  listProjectArtifactSessions: async (id: string): Promise<ArtifactSession[]> => {
    const { data } = await apiClient.get<ArtifactSession[]>(`${PROJECTS_URL}/${id}/artifact-sessions`);
    return data;
  },
};

export default projectService;
